import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import type { Project } from '../types';

/**
 * Choix d'un projet, avec recherche.
 *
 * Un `<select>` natif devient inutilisable passé quelques dizaines de projets : il faut
 * dérouler une liste alphabétique et reconnaître un code au vol. Ici on tape ce dont on se
 * souvient, code, nom du projet, nom du client ou numéro de commande, et les termes se
 * cumulent : « indigo prest » trouve « INDIGO PARK - Prestation Azure ».
 *
 * Le composant est contrôlé et ne connaît que l'identifiant choisi. Il ne filtre pas la
 * liste qu'on lui donne : c'est à l'appelant de décider quels projets sont proposés, un
 * projet clôturé n'ayant rien à faire là.
 */

export interface ProjectSelectExtra {
    id: string;
    label: string;
    /** Précision affichée en gris, par exemple « saisie fermée ». */
    note?: string;
}

interface ProjectSelectProps {
    projects: Project[];
    value: string;
    onChange: (projectId: string) => void;
    /**
     * Projet à proposer en plus de la liste. Sert aux saisies déjà enregistrées sur un
     * projet qui n'est plus proposé : sans cela le champ paraîtrait vide, et la ligne
     * changerait de projet au premier enregistrement.
     */
    extra?: ProjectSelectExtra | null;
    placeholder?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    /**
     * Nom accessible du champ. Les libellés voisins ne sont liés à rien dans les deux écrans
     * appelants, et un champ de saisie sans nom n'est pas annonçable.
     */
    ariaLabel?: string;
}

/** Minuscules, accents retirés : « Prestation » se trouve en tapant « prestation ». */
function fold(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

export function ProjectSelect({
    projects,
    value,
    onChange,
    extra = null,
    placeholder = 'Rechercher un projet…',
    autoFocus = false,
    disabled = false,
    ariaLabel = 'Projet',
}: ProjectSelectProps) {
    // Identifiant propre à l'instance : deux sélecteurs sur la même page ne doivent pas
    // partager l'identifiant de leur liste, ce qui rendrait aria-controls ambigu.
    const listId = useId();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    // -1 : aucune option active. Une liste qui s'ouvre avec sa première option déjà active
    // fait qu'une Entrée réflexe, ou une tabulation suivie d'Entrée pour enregistrer,
    // remplace le projet choisi par le premier de la liste. Tant que l'utilisateur n'a pas
    // appuyé sur une flèche ni tapé, rien n'est actif.
    const [highlight, setHighlight] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const options = useMemo(() => {
        const fromProjects = projects.map((project) => ({
            id: project.id,
            label: `${project.code} - ${project.name}`,
            hint: project.clientName || undefined,
            note: undefined as string | undefined,
            // Le numéro de commande n'est pas affiché, la ligne serait trop longue, mais il
            // reste cherchable : c'est souvent lui qu'on a sous les yeux.
            haystack: fold(
                [project.code, project.name, project.clientName, project.orderNumber]
                    .filter(Boolean)
                    .join(' ')
            ),
        }));

        if (extra && !projects.some((p) => p.id === extra.id)) {
            return [
                { id: extra.id, label: extra.label, hint: undefined, note: extra.note, haystack: fold(extra.label) },
                ...fromProjects,
            ];
        }
        return fromProjects;
    }, [projects, extra]);

    const selected = options.find((option) => option.id === value) || null;

    const filtered = useMemo(() => {
        const terms = fold(query).split(/\s+/).filter(Boolean);
        if (terms.length === 0) return options;
        return options.filter((option) => terms.every((term) => option.haystack.includes(term)));
    }, [options, query]);

    // Fermeture au clic à l'extérieur. `mousedown` et non `click` : un clic dans la liste
    // doit être traité par la liste, pas annulé par la fermeture.
    useEffect(() => {
        if (!open) return;
        const onMouseDown = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [open]);

    // L'élément survolé au clavier doit rester visible.
    useEffect(() => {
        if (!open) return;
        listRef.current?.querySelector('[data-highlighted="true"]')?.scrollIntoView({ block: 'nearest' });
    }, [highlight, open]);

    // Idempotent : la liste est déjà ouverte quand un second clic arrive après le focus, et
    // remettre la recherche à zéro effacerait ce qui vient d'être tapé.
    const openList = () => {
        if (disabled || open) return;
        setQuery('');
        setHighlight(-1);
        setOpen(true);
    };

    const choose = (id: string) => {
        onChange(id);
        setOpen(false);
        setQuery('');
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!open) {
                openList();
                return;
            }
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setHighlight((current) => {
                if (filtered.length === 0) return -1;
                // Depuis « rien d'actif », la flèche haute part de la fin de liste.
                if (current < 0) return step > 0 ? 0 : filtered.length - 1;
                return (current + step + filtered.length) % filtered.length;
            });
            return;
        }

        if (event.key === 'Enter' && open) {
            const option = highlight >= 0 ? filtered[highlight] : undefined;
            if (option) {
                // La touche ne doit pas enregistrer le formulaire : elle valide le choix.
                event.preventDefault();
                choose(option.id);
                return;
            }
            // Aucune option active : la liste se referme sans rien changer. Le formulaire
            // reçoit l'Entrée, ce qui est exactement ce qu'attend quelqu'un qui vient de
            // tabuler dans le champ pour enregistrer.
            setOpen(false);
            setQuery('');
            return;
        }

        if (event.key === 'Escape' && open) {
            event.preventDefault();
            setOpen(false);
            setQuery('');
        }
    };

    return (
        <div className="relative" ref={containerRef}>
            <div className="relative">
                {open
                    ? <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)] pointer-events-none" />
                    : <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)] pointer-events-none" />}
                <input
                    type="text"
                    role="combobox"
                    aria-expanded={open}
                    aria-autocomplete="list"
                    // `aria-controls` ne pointe vers la liste que lorsqu'elle existe, et
                    // `aria-activedescendant` restitue l'option parcourue au clavier : sans
                    // elle, un lecteur d'écran n'annonce rien pendant tout le parcours puis
                    // la valeur change sans explication.
                    aria-controls={open ? listId : undefined}
                    aria-activedescendant={
                        open && highlight >= 0 && filtered[highlight]
                            ? `${listId}-${filtered[highlight].id}`
                            : undefined
                    }
                    aria-label={ariaLabel}
                    value={open ? query : selected?.label || ''}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        // Taper une recherche désigne un candidat : la première option
                        // devient active, l'utilisateur ayant exprimé une intention.
                        setHighlight(0);
                        if (!open) setOpen(true);
                    }}
                    onFocus={openList}
                    onClick={openList}
                    onKeyDown={onKeyDown}
                    placeholder={selected ? selected.label : placeholder}
                    autoFocus={autoFocus}
                    disabled={disabled}
                    className={cn('input', open ? 'pl-9' : 'pr-9')}
                />
            </div>

            {open && (
                <ul
                    id={listId}
                    role="listbox"
                    aria-label={ariaLabel}
                    ref={listRef}
                    className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-lg"
                >
                    {filtered.length === 0 ? (
                        // `presentation` : ce n'est pas une option, et un `<li>` sans rôle
                        // dans une liste `listbox` n'a pas de sens pour un lecteur d'écran.
                        <li role="presentation" className="px-3 py-2 text-sm text-[var(--color-text-muted)]">
                            Aucun projet ne correspond
                        </li>
                    ) : (
                        filtered.map((option, index) => (
                            // Le clic est porté par l'option elle-même : un `<button>` à
                            // l'intérieur d'un `role="option"` est un descendant focusable,
                            // que le motif listbox interdit et qui ajoutait un arrêt de
                            // tabulation par projet.
                            <li
                                key={option.id}
                                id={`${listId}-${option.id}`}
                                role="option"
                                aria-selected={option.id === value}
                                data-highlighted={index === highlight}
                                onMouseEnter={() => setHighlight(index)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => choose(option.id)}
                                className={cn(
                                    'cursor-pointer px-3 py-2 text-sm flex items-center gap-2',
                                    index === highlight
                                        ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                                        : 'text-[var(--color-text)] hover:bg-[var(--color-bg)]'
                                )}
                            >
                                <span className="flex-1 min-w-0">
                                    <span className="block truncate">{option.label}</span>
                                    {(option.hint || option.note) && (
                                        <span className="block truncate text-xs text-[var(--color-text-muted)]">
                                            {option.hint}
                                            {option.hint && option.note ? ' · ' : ''}
                                            {option.note}
                                        </span>
                                    )}
                                </span>
                                {option.id === value && (
                                    <Check className="w-4 h-4 flex-shrink-0 text-[var(--color-primary)]" />
                                )}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
}
